/**
 * Drawer — right-side slide-in panel for secondary content surfaces.
 *
 * Used for ongoing-log content (Status Pulse, RAID Log) that doesn't
 * deserve prime on-page real estate but still needs to be reachable
 * fast. The pattern keeps the Overview page focused on "what is this
 * and is it OK" while the logs live one click away.
 *
 * Behavior:
 *   - Slides in from the right with a backdrop dim
 *   - ESC closes
 *   - Click outside (on the backdrop) closes
 *   - Locks body scroll while open (so the underlying page doesn't
 *     scroll under the drawer)
 *   - aria-modal + role="dialog" for accessibility
 *   - Optional `resizeKey`: drag the left edge to resize (double-click it
 *     to reset) or use the expand button; the width is remembered per key
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GripVertical, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/cn";

const MIN_WIDTH = 420;
/** Default width for resizable drawers (≈ max-w-xl). */
const DEFAULT_WIDTH = 576;
/** Space kept clear on the left so the backdrop stays clickable. */
const GUTTER = 48;

const maxWidth = () => Math.max(MIN_WIDTH, window.innerWidth - GUTTER);
const clamp = (w: number) => Math.min(Math.max(w, MIN_WIDTH), maxWidth());

/** Drawer width in px, persisted in localStorage under `drawer-width:<key>`. */
function useDrawerWidth(key: string | undefined, defaultWidth = DEFAULT_WIDTH) {
  const storageKey = key ? `drawer-width:${key}` : null;
  const [width, setWidthState] = useState<number>(() => {
    const saved = storageKey ? Number(localStorage.getItem(storageKey)) : NaN;
    return Number.isFinite(saved) && saved > 0 ? saved : defaultWidth;
  });
  const setWidth = useCallback(
    (w: number) => {
      const next = Math.round(clamp(w));
      setWidthState(next);
      if (storageKey) localStorage.setItem(storageKey, String(next));
    },
    [storageKey],
  );
  return [width, setWidth] as const;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Optional descriptive subtitle under the title (e.g. the
   *  entity ID + name for context). */
  subtitle?: React.ReactNode;
  /** Optional actions area in the header — e.g. "Add update". */
  headerActions?: React.ReactNode;
  /** Width of the panel; defaults to a comfortable reading column.
   *  Ignored when `resizeKey` is set. */
  widthClass?: string;
  /** Makes the drawer resizable; its width is remembered under this key. */
  resizeKey?: string;
  /** Resizable drawers: width before the person resizes it (and on reset). */
  defaultWidth?: number;
  /** Replaces the body's default padded, scrolling layout (for bodies that
   *  manage their own scrolling, e.g. side-by-side columns). */
  bodyClassName?: string;
  children: React.ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  headerActions,
  widthClass = "w-full max-w-xl",
  resizeKey,
  defaultWidth = DEFAULT_WIDTH,
  bodyClassName,
  children,
}: Props) {
  const [width, setWidth] = useDrawerWidth(resizeKey, defaultWidth);
  const [dragging, setDragging] = useState(false);
  const dragFrom = useRef<{ x: number; width: number } | null>(null);
  const expanded = resizeKey ? width >= maxWidth() - 1 : false;

  const onHandleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragFrom.current = { x: e.clientX, width: clamp(width) };
    setDragging(true);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (!dragFrom.current) return;
    // The panel is pinned right, so dragging left widens it.
    setWidth(dragFrom.current.width + dragFrom.current.x - e.clientX);
  };
  const onHandleUp = () => {
    dragFrom.current = null;
    setDragging(false);
  };

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Body-scroll lock while open. Restored on close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop — click to close. */}
          <motion.button
            type="button"
            aria-label="Close drawer"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/30 cursor-default"
          />

          {/* Panel — slides in from the right. */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className={cn(
              "absolute right-0 top-0 flex h-full flex-col bg-surface shadow-2xl ring-1 ring-line",
              resizeKey ? "max-w-full" : widthClass,
              dragging && "select-none",
            )}
            style={resizeKey ? { width: clamp(width) } : undefined}
          >
            {resizeKey && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize drawer"
                title="Drag to resize · double-click to reset"
                onPointerDown={onHandleDown}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                onDoubleClick={() => setWidth(defaultWidth)}
                className="group absolute inset-y-0 -left-1.5 z-10 w-3 cursor-col-resize"
              >
                <div
                  className={cn(
                    "mx-auto h-full w-0.5 transition-colors group-hover:bg-indigo-400",
                    dragging && "bg-indigo-500",
                  )}
                />
                {/* A visible grip so it's obvious the edge can be dragged. */}
                <div
                  className={cn(
                    "absolute left-1/2 top-1/2 flex h-10 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-line-strong bg-surface text-ink-subtle shadow-sm transition-colors group-hover:border-indigo-400 group-hover:text-indigo-500",
                    dragging && "border-indigo-500 text-indigo-500",
                  )}
                >
                  <GripVertical className="h-3.5 w-3.5" />
                </div>
              </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-ink">{title}</div>
                {subtitle && (
                  <div className="mt-0.5 truncate text-xs text-ink-muted">
                    {subtitle}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {headerActions}
                {resizeKey && (
                  <button
                    type="button"
                    onClick={() => setWidth(expanded ? defaultWidth : maxWidth())}
                    className="rounded-md p-2 text-ink-subtle hover:bg-raised hover:text-ink-2"
                    aria-label={expanded ? "Collapse drawer" : "Expand drawer"}
                    title={expanded ? "Collapse" : "Expand"}
                  >
                    {expanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md p-2 text-ink-subtle hover:bg-raised hover:text-ink-2"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body — fills, scrolls. */}
            <div className={bodyClassName ?? "flex-1 overflow-y-auto p-5"}>{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
